// ============================================================================
// JARVISTRAVEL - ALERT BANNER COMPONENT
// Each alert shows as a card ONCE. After that it lives in the bell.
// JT-UI-005: Alerts banner — Must requirement. JAR-2552.
// ============================================================================

import { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import type { BudgetOpenOn, Trip } from '@/app/types';
import { tripAlerts, type AlertAction, type AlertVariant, type TripAlert } from '@/app/utils/tripAlerts';
import { useBudgetIntelligence } from '@/app/hooks/useBudgetIntelligence';
import { markAlertsSeen, readAlertState } from '@/app/data/alertState';

interface AlertBannerProps {
  trip: Trip;
  /** Called when user clicks an alert action that leads to a tab (e.g. 'View Itinerary') */
  onNavigateToTab?: (tab: string) => void;
  /** Opens the budget. It is a sheet, not a tab: sent through onNavigateToTab
   *  as 'property', the budget actions opened the Property tab (JAR-2001).
   *  Set Budget asks for it to open on its total (JAR-2018). */
  onShowBudget?: (openOn?: BudgetOpenOn) => void;
  /** Fired when cards are first shown, so the parent can refresh the bell this
   *  session rather than only after a reload (JAR-2552). The parent re-reads the
   *  stored alert state, which this banner has already written, so no ids are
   *  passed (JAR-2634). */
  onAlertsSeen?: () => void;
}

const VARIANT_STYLES: Record<AlertVariant, { bg: string; border: string; text: string; iconBg: string }> = {
  danger: { bg: 'bg-danger/10', border: 'border-danger/20', text: 'text-danger', iconBg: 'bg-danger/15' },
  warning: { bg: 'bg-warning/10', border: 'border-warning/20', text: 'text-warning', iconBg: 'bg-warning/15' },
  info: { bg: 'bg-info/10', border: 'border-info/20', text: 'text-info', iconBg: 'bg-info/15' },
  success: { bg: 'bg-success/10', border: 'border-success/20', text: 'text-success', iconBg: 'bg-success/15' },
};

export function AlertBanner({ trip, onNavigateToTab, onShowBudget, onAlertsSeen }: AlertBannerProps) {
  // NOT memoised on `trip`, and that is deliberate.
  //
  // The 10-05 review asked for useMemo(() => tripAlerts(trip), [trip]) on cost
  // grounds. It is wrong here: tripAlerts reads todayYmd(), so it is not a pure
  // function of the trip. Memoising on the trip object alone freezes the Packed
  // Day alert against the clock, and "never speaks about today on a trip that
  // has not started" caught it — that test advances the clock and re-renders
  // the SAME trip, which the memo then refused to recompute.
  //
  // A correct key would have to include today's date, which buys nothing: the
  // work is a budget projection and one day's rating over an in-memory trip.
  // JAR-2935: the budget alert's projection now comes from core when this is a
  // core-backed trip (shared per-trip cache with the budget surfaces); the
  // device engine answers only when the hook has nothing — local/demo trip,
  // failure, or the request still in flight.
  const intelligence = useBudgetIntelligence(trip);
  // The list recomputes on every render, so a budget alert that only exists
  // once core's summary lands appears (and is marked seen) then — the
  // seen-map is keyed by alert id, not by render order (ocr-25).
  const alerts = tripAlerts(trip, intelligence.summary);

  // What storage says has already had its card (read once per trip, not every
  // render — the banner re-renders whenever the trip object changes, and this
  // does not change underneath it), and which cards the X has closed in THIS
  // session (storage already knows they were seen, so this only hides them now).
  //
  // Both are per-trip and reset on a trip switch at RENDER time, via a stored
  // previous id — not in an effect. An effect resets AFTER the switch's first
  // render, so that render filtered the new trip's alerts by the OLD trip's
  // seen/closed: a one-frame stale card (JAR-2634). Adjusting state during
  // render re-runs before commit, so there is no stale frame.
  const [seen, setSeen] = useState<Record<string, string>>(() => readAlertState(trip.id).seen);
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [shownTripId, setShownTripId] = useState(trip.id);
  if (shownTripId !== trip.id) {
    setShownTripId(trip.id);
    setSeen(readAlertState(trip.id).seen);
    setClosed(new Set());
  }

  const showing = alerts.filter((a) => !seen[a.id] && !closed.has(a.id));

  // Record the showing as a side effect, never during render. Writing to
  // storage while rendering would also fire under StrictMode's double render
  // and in a discarded concurrent render, retiring a card the user never saw.
  //
  // The dependency is the ids, not the array: tripAlerts rebuilds its objects
  // every render, so depending on the array would write on every render.
  // SORTED, so the key depends on the SET of ids and not their order: a
  // re-render that returned the same alerts in a different order would
  // otherwise change the string and re-run the effect (review 10-05).
  const showingIds = showing.map((a) => a.id).sort().join(',');

  // What this mount has already recorded. Without it, closing one card shrinks
  // `showing`, which changes `showingIds`, which re-runs the effect and
  // re-announces the remaining alerts to the parent — a storage write and a
  // parent state update for something already done (review 10-05, the HIGH).
  //
  // A ref rather than state: it must not itself cause a render, and it is read
  // and written inside the effect only.
  const marked = useRef<Set<string>>(new Set());
  useLayoutEffect(() => {
    marked.current = new Set();
  }, [trip.id]);

  const onAlertsSeenRef = useRef(onAlertsSeen);
  // Written in an effect, not during render: the render that carried it could
  // be discarded, and the effect below is what reads it.
  useLayoutEffect(() => {
    onAlertsSeenRef.current = onAlertsSeen;
  }, [onAlertsSeen]);

  useEffect(() => {
    if (!showingIds) return;
    const fresh = showingIds.split(',').filter((id) => !marked.current.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) marked.current.add(id);

    markAlertsSeen(trip.id, fresh);
    // Through a ref, so a parent passing an inline arrow — the common React
    // idiom — cannot re-run this on every one of its own renders. No argument:
    // the parent re-reads the state this just wrote (JAR-2634).
    onAlertsSeenRef.current?.();
    // Deliberately NOT setSeen here. Marking them seen mid-view would remove
    // the cards from under the person reading them. Storage is updated so the
    // next load is correct, and the cards stay until they are closed or the
    // screen is left.
  }, [trip.id, showingIds]);

  if (showing.length === 0) return null;

  // Which handler each action kind needs. One table, so runAction and canRun
  // cannot disagree: they used to be a switch and a ternary that both assumed
  // "anything not view-itinerary is a budget action", so a NEW kind would
  // render a button (onShowBudget is truthy) that did nothing on click
  // (review 10-05).
  //
  // Record<AlertAction, …> is the part that holds: adding a kind to
  // AlertAction without adding it here is a compile error, not a dead button.
  const handlers: Record<AlertAction, { available: boolean; run: () => void }> = {
    'review-budget': { available: !!onShowBudget, run: () => onShowBudget?.() },
    'set-budget': { available: !!onShowBudget, run: () => onShowBudget?.('total') },
    'view-itinerary': { available: !!onNavigateToTab, run: () => onNavigateToTab?.('itinerary') },
  };

  // An action whose handler was not supplied is not offered, which is what the
  // old `action: onShowBudget ? … : undefined` did at build time.
  const canRun = (alert: TripAlert) => !!alert.action && handlers[alert.action.kind].available;

  return (
    <div className="space-y-2 px-4 py-2">
      {showing.map((alert) => {
        const styles = VARIANT_STYLES[alert.variant];
        const Icon = alert.icon;
        return (
          <div
            key={alert.id}
            className={`flex items-start gap-3 p-3 rounded-xl border ${styles.bg} ${styles.border} animate-reveal`}
          >
            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${styles.iconBg} ${styles.text}`}>
              <Icon className="w-4 h-4" strokeWidth={1.75} />
            </div>

            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${styles.text}`}>{alert.title}</p>
              <p className={`text-xs mt-0.5 ${styles.text} opacity-80`}>{alert.message}</p>
              {alert.action && canRun(alert) && (
                <button
                  onClick={() => handlers[alert.action!.kind].run()}
                  className={`text-xs font-medium mt-1.5 underline ${styles.text} hover:opacity-80`}
                >
                  {alert.action.label}
                </button>
              )}
            </div>

            <button
              onClick={() => setClosed((prev) => new Set(prev).add(alert.id))}
              className={`flex-shrink-0 p-1 rounded-full tap-44 ${styles.text} hover:opacity-60 transition-opacity`}
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
